import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppBar, Badge, Box, Button, Divider, Drawer, IconButton, List, ListItemButton, ListItemText, Menu, MenuItem, Popover, Toolbar, Typography, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import logo from '../../assets/logo.svg';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/userService';

const BASE_NAV_ITEMS = [
  { label: 'Discover', path: '/discover' },
  { label: 'Pulse', path: '/pulse' },
];

const PORTFOLIO_ITEMS = [
  { label: 'Positions', path: '/portfolio/positions' },
  { label: 'Wheel Strategies', path: '/portfolio/wheel' },
  { label: 'Earnings Plays', path: '/portfolio/earnings' },
  { label: 'Gamma Exposure', path: '/portfolio/gamma-exposure' },
];

const SIGNAL_REPORT_ITEMS = [
  { label: 'Pulse', path: '/signal-report/pulse' },
  { label: 'Earnings', path: '/signal-report/earnings' },
];

const SETTINGS_ITEMS = [
  { label: 'Account', path: '/settings' },
  { label: 'Trader Preferences', path: '/settings/preferences' },
  { label: 'Add Ticker', path: '/settings/add-ticker' },
];

const ADMIN_ITEMS = [
  { label: 'Ticker Watchlist', path: '/admin/tickers' },
  { label: 'Manual Triggers', path: '/admin/jobs' },
  { label: 'Max Pain Explorer', path: '/admin/max-pain' },
  { label: 'Paper Trades', path: '/admin/paper-trades' },
  { label: 'Newsletter', path: '/admin/newsletter' },
  { label: 'Terms & Conditions', path: '/admin/terms' },
  { label: 'Pulse Scorecard', path: '/admin/pulse-scorecard' },
  { label: 'System Info', path: '/admin/system' },
];

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleColorMode } = useTheme();
  const { currentUser, appUser } = useAuth();
  const [adminAnchor, setAdminAnchor] = useState<null | HTMLElement>(null);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const [portfolioAnchor, setPortfolioAnchor] = useState<null | HTMLElement>(null);
  const [signalAnchor, setSignalAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Notifications
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [notifCount, setNotifCount] = useState(0);
  const [notifItems, setNotifItems] = useState<Array<{ id: number; ticker: string; title: string; message: string; link: string | null; created_at: string }>>([]);
  const notifPolling = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifCount = useCallback(() => {
    if (!currentUser) return;
    userService.getNotifications()
      .then((data) => { setNotifCount(data.unread_count); setNotifItems(data.items); })
      .catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    fetchNotifCount();
    notifPolling.current = setInterval(fetchNotifCount, 60000);
    return () => { if (notifPolling.current) clearInterval(notifPolling.current); };
  }, [fetchNotifCount]);

  const handleNotifOpen = (e: React.MouseEvent<HTMLElement>) => {
    setNotifAnchor(e.currentTarget);
    if (notifCount > 0) {
      userService.markNotificationsRead().then(() => setNotifCount(0)).catch(() => {});
    }
  };

  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const isAdminActive = location.pathname.startsWith('/admin');
  const isSettingsActive = location.pathname.startsWith('/settings');

  const handleDrawerNav = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mr: { xs: 1, sm: 4 } }}
          onClick={() => navigate('/')}
        >
          <img src={logo} alt="Pryvo" width={32} height={32} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
            Pryvo
          </Typography>
        </Box>

        {/* Desktop nav buttons */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
          {BASE_NAV_ITEMS.map((item) => (
            <Button
              key={item.path}
              color="inherit"
              onClick={() => navigate(item.path)}
              sx={{
                fontWeight: location.pathname.startsWith(item.path) ? 700 : 400,
                borderBottom: location.pathname.startsWith(item.path) ? '2px solid white' : 'none',
                borderRadius: 0,
              }}
            >
              {item.label}
            </Button>
          ))}

          {/* Portfolio dropdown */}
          <Button
            color="inherit"
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setPortfolioAnchor(e.currentTarget)}
            sx={{
              fontWeight: location.pathname.startsWith('/portfolio') ? 700 : 400,
              borderBottom: location.pathname.startsWith('/portfolio') ? '2px solid white' : 'none',
              borderRadius: 0,
            }}
          >
            Portfolio
          </Button>
          <Menu
            anchorEl={portfolioAnchor}
            open={!!portfolioAnchor}
            onClose={() => setPortfolioAnchor(null)}
          >
            {PORTFOLIO_ITEMS.map((item) => (
              <MenuItem
                key={item.path}
                selected={location.pathname.startsWith(item.path)}
                onClick={() => { navigate(item.path); setPortfolioAnchor(null); }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Signal Report dropdown */}
          <Button
            color="inherit"
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setSignalAnchor(e.currentTarget)}
            sx={{
              fontWeight: location.pathname.startsWith('/signal-report') ? 700 : 400,
              borderBottom: location.pathname.startsWith('/signal-report') ? '2px solid white' : 'none',
              borderRadius: 0,
            }}
          >
            Signal Report
          </Button>
          <Menu
            anchorEl={signalAnchor}
            open={!!signalAnchor}
            onClose={() => setSignalAnchor(null)}
          >
            {SIGNAL_REPORT_ITEMS.map((item) => (
              <MenuItem
                key={item.path}
                selected={location.pathname === item.path}
                onClick={() => { navigate(item.path); setSignalAnchor(null); }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>

          <Button
            color="inherit"
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setSettingsAnchor(e.currentTarget)}
            sx={{
              fontWeight: isSettingsActive ? 700 : 400,
              borderBottom: isSettingsActive ? '2px solid white' : 'none',
              borderRadius: 0,
            }}
          >
            Settings
          </Button>
          <Menu
            anchorEl={settingsAnchor}
            open={!!settingsAnchor}
            onClose={() => setSettingsAnchor(null)}
          >
            {SETTINGS_ITEMS.map((item) => (
              <MenuItem
                key={item.path}
                selected={location.pathname === item.path}
                onClick={() => { navigate(item.path); setSettingsAnchor(null); }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>

          {appUser?.role === 'admin' && (
            <>
              <Button
                color="inherit"
                endIcon={<ExpandMoreIcon />}
                onClick={(e) => setAdminAnchor(e.currentTarget)}
                sx={{
                  fontWeight: isAdminActive ? 700 : 400,
                  borderBottom: isAdminActive ? '2px solid white' : 'none',
                  borderRadius: 0,
                }}
              >
                Admin
              </Button>
              <Menu
                anchorEl={adminAnchor}
                open={!!adminAnchor}
                onClose={() => setAdminAnchor(null)}
              >
                {ADMIN_ITEMS.map((item) => (
                  <MenuItem
                    key={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => { navigate(item.path); setAdminAnchor(null); }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>

        {/* Mobile spacer */}
        <Box sx={{ flexGrow: 1, display: { xs: 'flex', sm: 'none' } }} />

        {currentUser && (
          <IconButton color="inherit" onClick={handleNotifOpen} sx={{ mr: 1 }} aria-label="Notifications">
            <Badge badgeContent={notifCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        )}
        <Popover
          open={!!notifAnchor}
          anchorEl={notifAnchor}
          onClose={() => setNotifAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ width: 360 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Notifications</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {notifItems.length > 0 && (
                  <Button
                    size="small"
                    color="error"
                    sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0 }}
                    onClick={async () => {
                      await userService.deleteNotifications();
                      setNotifCount(0);
                      setNotifItems([]);
                    }}
                  >
                    Clear All
                  </Button>
                )}
                <Button
                  size="small"
                  sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0 }}
                  onClick={() => { setNotifAnchor(null); navigate('/alerts'); }}
                >
                  View All
                </Button>
              </Box>
            </Box>
            <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
              {notifItems.length === 0 ? (
                <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
                  No notifications
                </Typography>
              ) : (
                <List disablePadding>
                  {notifItems.map((n) => (
                    <ListItemButton
                      key={n.id}
                      onClick={() => {
                        setNotifAnchor(null);
                        if (n.link) navigate(n.link);
                      }}
                      sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      <ListItemText
                        primary={n.title}
                        secondary={n.message}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Popover>

        <IconButton color="inherit" onClick={toggleColorMode} sx={{ mr: 1 }} aria-label="Toggle theme">
          {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>

        {currentUser && !isMobile && (
          <Button color="inherit" onClick={handleLogout} size="small">
            Logout
          </Button>
        )}

        {/* Mobile hamburger */}
        <IconButton
          color="inherit"
          onClick={() => setDrawerOpen(true)}
          sx={{ display: { xs: 'flex', sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 240, pt: 1 }} role="presentation">
          <List>
            {BASE_NAV_ITEMS.map((item) => (
              <ListItemButton
                key={item.path}
                selected={location.pathname.startsWith(item.path)}
                onClick={() => handleDrawerNav(item.path)}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
            <Divider />
            {PORTFOLIO_ITEMS.map((item) => (
              <ListItemButton
                key={item.path}
                selected={location.pathname.startsWith(item.path)}
                onClick={() => handleDrawerNav(item.path)}
              >
                <ListItemText primary={item.label} secondary="Portfolio" />
              </ListItemButton>
            ))}
            <Divider />
            {SIGNAL_REPORT_ITEMS.map((item) => (
              <ListItemButton
                key={item.path}
                selected={location.pathname === item.path}
                onClick={() => handleDrawerNav(item.path)}
              >
                <ListItemText primary={item.label} secondary="Signal Report" />
              </ListItemButton>
            ))}
            <Divider />
            {SETTINGS_ITEMS.map((item) => (
              <ListItemButton
                key={item.path}
                selected={location.pathname === item.path}
                onClick={() => handleDrawerNav(item.path)}
              >
                <ListItemText primary={item.label} secondary="Settings" />
              </ListItemButton>
            ))}
            {appUser?.role === 'admin' && (
              <>
                <Divider />
                {ADMIN_ITEMS.map((item) => (
                  <ListItemButton
                    key={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => handleDrawerNav(item.path)}
                  >
                    <ListItemText primary={item.label} secondary="Admin" />
                  </ListItemButton>
                ))}
              </>
            )}
          </List>
          {currentUser && (
            <>
              <Divider />
              <List>
                <ListItemButton onClick={() => { setDrawerOpen(false); handleLogout(); }}>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </List>
            </>
          )}
        </Box>
      </Drawer>
    </AppBar>
  );
};
